import { motion } from "framer-motion";
import { Trophy, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatEur } from "@/lib/index";

interface IVAModuleProps {
  winnerName: string;
  winnerNetCost: number;
  loserNetCost: number;
  vatRate: number;
  vatAmount: number;
  totalWithVat: number;
  loserTotalWithVat: number;
}

export function IVAModule({
  winnerName,
  winnerNetCost,
  loserNetCost,
  vatRate,
  vatAmount,
  totalWithVat,
  loserTotalWithVat
}: IVAModuleProps) {
  const loserVatAmount = (loserNetCost * vatRate) / 100;
  const savingsWithVat = loserTotalWithVat - totalWithVat;

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 35, delay: 0.2 }}
    >
      <Card className="border-2 border-accent/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-success/10">
                <Trophy className="h-6 w-6 text-success" />
              </div>
              <div>
                <CardTitle className="text-2xl">Rentabilidad con IVA</CardTitle>
                <CardDescription className="text-base mt-1">
                  Análisis de costes totales incluyendo impuestos
                </CardDescription>
              </div>
            </div>
            <Badge className="bg-accent text-accent-foreground text-base px-4 py-2">
              IVA {vatRate}%
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold">Proveedor</th>
                  <th className="text-right py-3 px-4 font-semibold">Coste Neto</th>
                  <th className="text-right py-3 px-4 font-semibold">IVA ({vatRate}%)</th>
                  <th className="text-right py-3 px-4 font-semibold">Total con IVA</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-border bg-success/10">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-success" />
                      <span className="font-semibold text-success">{winnerName}</span>
                    </div>
                  </td>
                  <td className="text-right py-4 px-4 font-mono text-success">
                    {formatEur(winnerNetCost)}
                  </td>
                  <td className="text-right py-4 px-4 font-mono text-success">
                    {formatEur(vatAmount)}
                  </td>
                  <td className="text-right py-4 px-4">
                    <span className="text-xl font-bold text-success">
                      {formatEur(totalWithVat)}
                    </span>
                  </td>
                </tr>
                <tr className="border-b border-border bg-destructive/10">
                  <td className="py-4 px-4">
                    <span className="font-semibold text-destructive">
                      {winnerName === "Proveedor 1" ? "Proveedor 2" : "Proveedor 1"}
                    </span>
                  </td>
                  <td className="text-right py-4 px-4 font-mono text-destructive">
                    {formatEur(loserNetCost)}
                  </td>
                  <td className="text-right py-4 px-4 font-mono text-destructive">
                    {formatEur(loserVatAmount)}
                  </td>
                  <td className="text-right py-4 px-4">
                    <span className="text-xl font-bold text-destructive">
                      {formatEur(loserTotalWithVat)}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 300, damping: 35, delay: 0.3 }}
            className="bg-gradient-to-br from-success/10 via-primary/5 to-accent/10 rounded-xl p-8 space-y-4"
          >
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground font-medium">
                Diferencia Total con IVA
              </p>
              <div className="flex items-center justify-center gap-3">
                <TrendingUp className="h-8 w-8 text-success" />
                <span className="text-5xl font-bold text-success">
                  {formatEur(savingsWithVat)}
                </span>
              </div>
              <p className="text-base text-muted-foreground">
                Ahorro anual eligiendo {winnerName}
              </p>
            </div>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
